import { useReduxState } from 'hooks';
import { attributesExpressionOptions } from 'lib/customAttributes';
import { EmptyIndicator, MoreOptions } from 'components/common';
import { Box, BoxRow } from 'components/common/box';
import { GroupUserAttributeDisplayValue } from 'components/common/customAttributes';
import { GroupUserAttributesAddOrEdit, GroupUserAttributesRemove } from '.';

import type { AttributeCondition, UserCustomAttribute } from 'shared-types';

interface Props {
  disabled?: boolean;
  onAddUserAttribute: (conditions: AttributeCondition[]) => Promise<void>;
  onEditUserAttribute: (
    conditions: AttributeCondition[],
    conditionsIndex: number
  ) => Promise<void>;
  onRemoveUserAttribute: (index: number) => Promise<boolean>;
  disabledOverlayContent?: string;
  title?: string;
  tooltip?: string;
  userAttributes: AttributeCondition[][];
}

const GroupUserAttributes = ({
  disabled = false,
  onAddUserAttribute,
  onEditUserAttribute,
  onRemoveUserAttribute,
  disabledOverlayContent,
  title,
  tooltip,
  userAttributes
}: Props) => {
  const {
    language,
    messages,
    serverConfig: { isDemoEnvironment, userCustomAttributes },
    theme
  } = useReduxState();

  const expressions = attributesExpressionOptions({ messages, type: 'all' });
  const expressionsByValue = {};
  expressions.forEach((expression) => {
    expressionsByValue[expression.value] = expression.label;
  });

  const userCustomAttributesByKey: { [key: string]: UserCustomAttribute } = {};
  const userCustomAttributesTitlesByKey: { [key: string]: string } = {};
  userCustomAttributes?.forEach((attribute) => {
    userCustomAttributesByKey[attribute.key] = attribute;
    userCustomAttributesTitlesByKey[attribute.key] =
      attribute.titleTranslations?.[language] || attribute.title;
  });

  return (
    <div className="attributes">
      <Box
        disabledOverlayContent={disabledOverlayContent || null}
        headerContent={
          <GroupUserAttributesAddOrEdit
            disabled={disabled}
            onSubmit={onAddUserAttribute}
          />
        }
        headerContentWrap={true}
        padding={0}
        title={title || messages.userAttributes}
        titleTooltip={tooltip}
      >
        {isDemoEnvironment ? (
          <EmptyIndicator
            icon="exclamation-triangle"
            size="small"
            title={messages.commonDisabled}
            description={messages.commonDisabledInDemo}
          />
        ) : userAttributes?.length ? (
          userAttributes.map((conditions, i) => (
            <BoxRow key={i} padding={0}>
              <div className="attribute">
                {i !== 0 ? (
                  <div className="attribute-or">
                    <div className="attribute-or-content">
                      {messages.commonOr}
                    </div>
                  </div>
                ) : null}

                <div className="row row-center">
                  <div className="col">
                    {conditions.map((condition, j) => (
                      <div className="condition" key={j}>
                        {j !== 0 ? (
                          <div className="pre">{messages.commonAnd}</div>
                        ) : null}

                        <div className="content">
                          <div className="key">
                            {{
                              emailDomain: messages.userAttributesEmailDomain,
                              accountType: messages.usersAccountType,
                              ...userCustomAttributesTitlesByKey
                            }[condition.key] || condition.key}
                          </div>

                          <div className="post">
                            {
                              expressionsByValue[
                                condition.expression || 'equals'
                              ]
                            }
                          </div>

                          {!condition.expression ||
                          !['any', 'empty'].includes(condition.expression) ? (
                            <div className="value">
                              <GroupUserAttributeDisplayValue
                                conditionKey={condition.key}
                                conditionValue={condition.value}
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>

                  {disabled ? null : (
                    <div className="col-min">
                      <MoreOptions styleContentButtons={false}>
                        <GroupUserAttributesAddOrEdit
                          conditions={conditions}
                          conditionsIndex={i}
                          isEdit={true}
                          onSubmit={(newConditions) =>
                            onEditUserAttribute(newConditions, i)
                          }
                        />
                        <GroupUserAttributesRemove
                          onRemove={() => onRemoveUserAttribute(i)}
                        />
                      </MoreOptions>
                    </div>
                  )}
                </div>
              </div>
            </BoxRow>
          ))
        ) : (
          <EmptyIndicator
            icon="equals"
            size="small"
            title={messages.userAttributesConditionsEmpty}
          />
        )}
      </Box>

      <style jsx>{`
        .attribute {
          padding: 20px 15px;
          position: relative;
        }
        .attribute-or {
          position: absolute;
          top: -12px;
          left: 0;
          right: 0;
          text-align: center;
        }
        .attribute-or-content {
          background-color: ${theme.lightGrey};
          border-radius: 20px;
          color: ${theme.textColorLight};
          display: inline-block;
          font-size: 1.4rem;
          font-weight: 700;
          line-height: 1;
          padding: 4px 7px 6px;
        }
        .condition {
          align-items: center;
          display: inline-flex;
          font-size: 1.4rem;
          margin-right: 10px;
        }
        .condition:not(:first-child) {
          margin-top: 5px;
        }
        .condition:not(:first-child)::before {
          margin-top: 5px;
        }
        .pre {
          margin-right: 10px;
        }
        .content {
          align-items: center;
          background-color: ${theme.lightGrey};
          border-radius: 4px;
          display: inline-flex;
          padding: 2px 6px;
        }
        .key {
          font-weight: 600;
        }
        .post {
          color: ${theme.textColorLighter};
          margin: 0 3px;
        }
        .value {
          font-weight: 700;
        }
      `}</style>
    </div>
  );
};

export default GroupUserAttributes;
